import {
  LitElement,
  html,
  css,
  ifDefined,
  classMap,
  broadcastReceiver,
} from '@hh/global.js'

class HHDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    /*
     side prop is used to set from which side of the screen the drawer opens.
     It can be set to left or right.
     Default is right.
    */
    side: {
      type: String,
      reflect: true,
    },
    /*
      labelledBy and describedBy props to are used to pass and set aria-labelledby and aria-describedby on the dialog element without interfering actual aria functionality
    */
    labelledBy: {
      type: String,
      attribute: 'labelledby',
    },
    describedBy: {
      type: String,
      attribute: 'describedby',
    },
    openerSelector: {
      type: String,
      attribute: 'opener-selector',
    },
    // Display exit intent popup when trying to close the drawer
    confirmClose: {
      type: Boolean,
      default: false,
      attribute: 'confirm-close'
    },
    confirmPopupId: {
      type: String,
      attribute: 'confirm-popup-id'
    }
  }

  constructor() {
    super()

    this.isReducedMotion = matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (!this.side) {
      this.side = 'right'
    }
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('drawer', (...args) => {
      this.openFromDocument(...args)
    })

    window.addEventListener('keydown', this.onEscKey.bind(this))
    window.addEventListener('drawer:close', this.closeFromEvent.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver && this.receiver.disconnect()

    window.removeEventListener('keydown', this.onEscKey)
    window.removeEventListener('drawer:close', this.closeFromEvent)
  }

  get drawer() {
    return this.shadowRoot.querySelector('.drawer') ?? null
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      if (this.open) {
        document.documentElement.classList.add('overflow-hidden')
      } else {
        document.documentElement.classList.remove('overflow-hidden')
      }
    }
  }

  openDrawer() {
    this.open = true

    this.dispatchEvent(
      new CustomEvent('drawer:open', {
        bubbles: true,
        composed: true,
      })
    )
  }

  closeDrawer(force = false) {
    if (this.confirmClose && !force) {
      document.body.setAttribute('modal', JSON.stringify({
        id: this.confirmPopupId
      }))

      return
    }

    this.open = false

    if (this.openerSelector) {
      const opener = document.querySelector(this.openerSelector)
      opener?.focus({
        preventScroll: true,
        focusVisible: false,
      })
    }
  }

  // Handlers
  openFromDocument(data) {
    if (this.id === data.id) {
      this.openDrawer()
    }
  }

  onCloseClick() {
    this.closeDrawer(false)
  }

  closeFromEvent() {
    this.closeDrawer(true)
  }

  onEscKey(e) {
    if (e.key === 'Escape' && this.open) {
      this.closeDrawer(false)
    }
  }

  render() {
    return html`
      <div
        class=${classMap({
      overlay: true,
      open: this.open,
      'reduced-motion': this.isReducedMotion,
    })}
        @click=${this.onCloseClick}
        aria-hidden="true"
      ></div>
      <div
        class=${classMap({
      drawer: true,
      open: this.open,
      'reduced-motion': this.isReducedMotion,
    })}
        role="dialog"
        aria-modal="true"
        aria-labelledby=${ifDefined(this.labelledBy)}
        aria-describedby=${ifDefined(this.describedBy)}
        ?inert=${!this.open}
      >
        <hh-focus-trap active="${ifDefined(this.open)}">
          <slot name="close" @click=${this.onCloseClick}></slot>
          <slot></slot>
        </hh-focus-trap>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .overlay {
      position: fixed;
      inset: 0;
      z-index: 109;
      background-color: var(--hh-overlay-color, rgba(0, 0, 0, 0.5));
      backdrop-filter: blur(2.5px);
      -webkit-backdrop-filter: blur(2.5px);
      opacity: 0;
      pointer-events: none;
      transition: opacity var(--hh-duration-default) ease-in-out;
      min-width: 100%;
      min-height: 100%;
      width: 100vw;
      height: 100vh;
    }

    .overlay.open {
      opacity: 1;
      pointer-events: auto;
    }

    .drawer {
      position: fixed;
      top: 0;
      z-index: 110;
      height: 100%;
      width: 100%;
      max-width: var(--hh-max-width, 65rem);
      max-height: 100vh;
      background-color: var(--hh-background-color, var(--hh-color-day, #fff));
      overflow-y: auto;
      transition: transform var(--hh-duration-long) var(--hh-cubic-1);
    }

    :host([size='xl']) .drawer {
      max-width: 140.4rem;
    }

    :host([side='left']) .drawer {
      right: auto;
      left: 0;
      transform: translate3d(-100%, 0, 0);
    }

    :host([side='right']) .drawer {
      right: 0;
      left: auto;
      transform: translate3d(100%, 0, 0);
    }

    :host .drawer.open {
      transform: translate3d(0, 0, 0);
    }

    ::slotted([slot='close']) {
      position: absolute;
      cursor: pointer;
      top: var(--hh-close-top, 2.1rem);
      right: var(--hh-close-right, 1.8rem);
      color: var(--hh-close-color, inherit);
      background-color: var(--hh-close-background-color, transparent);
      border: var(--hh-close-border, none);
      padding: var(--hh-close-padding, 0.4rem 1rem);
      border-radius: var(--hh-close-border-radius, 3.5rem);
      font: var(--hh-close-font, inherit);
      transition: transform 0.7s ease;
      z-index: 5;
    }

    ::slotted([slot='close']:hover) {
      transform: scale(1.2);
    }

    .drawer.reduced-motion,
    .overlay.reduced-motion {
      transition: none;
    }
  `
}

customElements.define('hh-drawer', HHDrawer)
